import { useEffect } from "react";
import { QuantityStepper } from "@/components/quantityStepper/quantityStepper";
import { ModifierRuleBadge } from "@/components/modifierRuleBadge/modifierRuleBadge";
import { ModifierGroup } from "@/dto/modifierGroup";
import { Modifier } from "@/dto/modifier";
import { applyDataTestId } from "@/utils/testUtils";
import pom from "@/utils/pom";
import { useCurrency } from "@/app/providers/currencyContext";
import useMediaQuery from "@/utils/useMediaQuery";

export const ModifierGroupDisplay = ({
  selectableModifierGroups,
  selectedItem,
  selectedModifiers,
  handleModifierChange,
  handleModifierQuantityChange,
  merchantColor,
  isAddtocartClicked,
}: any) => {
  const isMobileScreen = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    if (isAddtocartClicked) {
      const firstUnmetGroup: ModifierGroup | undefined =
        selectableModifierGroups.find((group: any) => {
          const groupSelections: { [key: string]: number } =
            selectedModifiers[group.id] || {};
          const totalGroupQuantity = Object.values(groupSelections).reduce(
            (acc: number, quantity: number) => acc + quantity,
            0
          );
          return !(
            totalGroupQuantity >= group.minRequired &&
            totalGroupQuantity <= group.maxAllowed
          );
        }) as ModifierGroup | undefined;

      if (firstUnmetGroup) {
        const element = document.getElementById(firstUnmetGroup.id);
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: isMobileScreen ? "start" : "nearest",
          });
        }
      }
    }
  }, [
    isAddtocartClicked,
    selectedItem,
    selectedModifiers,
    selectableModifierGroups,
  ]);

  return (
    <>
      {selectableModifierGroups.map((group: any) => {
        const groupSelections: { [key: string]: number } =
          selectedModifiers[group.id] || ({} as Modifier[]);
        const totalGroupQuantity: number = Object.values(
          groupSelections
        ).reduce((acc: number, quantity: number) => acc + quantity, 0);
        const isRequirementMet: boolean =
          totalGroupQuantity >= group.minRequired &&
          totalGroupQuantity <= group.maxAllowed;

        return (
          <div key={group.id} id={group.id} className="mb-4">
            <label className="block font-medium text-base mb-2">
              <div className="flex justify-between text-gray-500 mb-4">
                <div className="font-semibold text-[13px] text-[#727272]">
                  {group.name.toUpperCase()}
                </div>
                <ModifierRuleBadge
                  min={group.minRequired}
                  max={group.maxAllowed}
                  isRequirementMet={isRequirementMet}
                  isAddtocartClicked={isAddtocartClicked}
                  modifierGroupId={group.id}
                />
              </div>
            </label>
            <div>
              <ModifierSelection
                {...{
                  group,
                  selectedModifiers,
                  handleModifierChange,
                  handleModifierQuantityChange,
                  merchantColor,
                  groupSelections,
                  totalGroupQuantity,
                }}
              />
            </div>
          </div>
        );
      })}
    </>
  );
};

const ModifierSelection = ({
  group,
  selectedModifiers,
  handleModifierChange,
  handleModifierQuantityChange,
  merchantColor,
  groupSelections,
  totalGroupQuantity,
}: any) => {
  const { format } = useCurrency();
  const isMaxReached: boolean = totalGroupQuantity >= group.maxAllowed;
  return (
    <>
      {group.modifiers.map((modifier: any) => {
        const isSingleSelection: boolean =
          group.minRequired === 1 && group.maxAllowed === 1;
        const isSelected: boolean = !!groupSelections[modifier?.id] as boolean;
        const isDisabled: boolean =
          (!isSingleSelection && isMaxReached && !isSelected) ||
          (!modifier.available as boolean);
        const modifierQuantity: number = groupSelections[modifier?.id] || 1;
        const disablePlus: boolean =
          modifierQuantity >= Math.min(group.maxAllowed, 99) ||
          totalGroupQuantity >= group.maxAllowed;

        return (
          <div
            key={modifier.id}
            className={`mb-4 pb-4 border-b border-[#E5E5E5] ${
              isDisabled ? "text-grayDisabled cursor-not-allowed" : ""
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {isSingleSelection && (
                  <SingleSelectionModifier
                    group={group}
                    modifier={modifier}
                    isSelected={isSelected}
                    handleModifierChange={handleModifierChange}
                    isSingleSelection={isSingleSelection}
                    isDisabled={isDisabled}
                    merchantColor={merchantColor}
                  />
                )}
                {!isSingleSelection && (
                  <MultipleSelectionModifier
                    group={group}
                    modifier={modifier}
                    isSelected={isSelected}
                    handleModifierChange={handleModifierChange}
                    isSingleSelection={isSingleSelection}
                    isDisabled={isDisabled}
                    merchantColor={merchantColor}
                    selectedModifiers={selectedModifiers}
                  />
                )}
                <button
                  className={`${
                    isDisabled
                      ? "text-grayDisabled cursor-not-allowed"
                      : "text-black cursor-pointer"
                  }`}
                  onClick={() =>
                    !isDisabled &&
                    handleModifierChange(
                      group.id,
                      modifier.id,
                      isSingleSelection
                    )
                  }
                >
                  {modifier.name}
                </button>
              </div>
              <span
                data-testid={
                  applyDataTestId(pom.itemPage.modifierPrice) + modifier.id
                }
                className={`text-right font-normal text-[15px] ${
                  isDisabled
                    ? "text-grayDisabled"
                    : isSelected
                    ? "text-black"
                    : "text-grayDark"
                }`}
              >
                +{format(modifier.price)}
              </span>
            </div>
            {!isSingleSelection && isSelected && (
              <span className={`pl-7 mt-2 block`}>
                <QuantityStepper
                  handleQuantityChange={(newQuantity: number) =>
                    handleModifierQuantityChange(
                      group.id,
                      modifier.id,
                      newQuantity
                    )
                  }
                  quantity={modifierQuantity}
                  disablePlus={disablePlus}
                  max={group.maxAllowed}
                  totalQuantity={totalGroupQuantity}
                  modifierId={modifier.id}
                  adaName={modifier?.name}
                />
              </span>
            )}
          </div>
        );
      })}
    </>
  );
};

const SingleSelectionModifier = ({
  group,
  modifier,
  isSelected,
  handleModifierChange,
  isSingleSelection,
  isDisabled,
  merchantColor,
}: any) => {
  return (
    <input
      type="radio"
      name={group.id}
      value={modifier.id}
      checked={isSelected && !isDisabled}
      disabled={isDisabled}
      onChange={() =>
        handleModifierChange(group.id, modifier.id, isSingleSelection)
      }
      className="mr-2"
      style={{
        width: "20px",
        height: "20px",
        cursor: isDisabled ? "not-allowed" : "pointer",
        accentColor: merchantColor,
        flexShrink: 0,
      }}
      data-testid={applyDataTestId(pom.itemPage.modifierInput) + modifier.id}
    />
  );
};

const MultipleSelectionModifier = ({
  group,
  modifier,
  isSelected,
  handleModifierChange,
  isSingleSelection,
  isDisabled,
  merchantColor,
}: any) => {
  return (
    <input
      type="checkbox"
      name={group.id}
      value={modifier.id}
      checked={isSelected && !isDisabled}
      onChange={() =>
        handleModifierChange(group.id, modifier.id, isSingleSelection)
      }
      disabled={isDisabled}
      className="mr-2"
      style={{
        width: "20px",
        height: "20px",
        cursor: isDisabled ? "not-allowed" : "pointer",
        accentColor: merchantColor,
        flexShrink: 0,
      }}
      data-testid={applyDataTestId(pom.itemPage.modifierInput) + modifier.id}
    />
  );
};
